import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Mentetlen-változás védelem.
 *
 * MIÉRT: a webes UX-elemzés (6.2) szerint a felület több helyen ELDOBJA a beírt, még nem mentett
 * adatot: fül-váltásnál, a jelenléti íven dátum-/eseményváltásnál, az adatlapon és a hosszú
 * dialógusokban. A jelenléti íven ez a klub napi munkájában jelentkezik (egy edző 15+ játékos
 * pipáit és a beszedett összegeket vezeti), ezért ez az egyetlen ADATVESZTŐ tétel a listán.
 *
 * HOGYAN: a képernyő annyit tesz, hogy bejelenti, van-e most mentetlen változása
 * (`useUnsavedChanges(isDirty, 'Jelenléti ív')`). A kilépési pontok (fül-váltás, képernyőn belüli
 * váltás, dialógus-bezárás) a `useUnsavedGuard()`-dal kérdezik meg, hogy szabad-e továbbmenni —
 * ha nem, a KÖZÖS megerősítő ablak jön fel (ConfirmContext), nem natív dialógus.
 *
 * A ROUTER ELŐTTI ELFOGÁS — mit fed le és mit NEM [frissítve: Web UX 3. csomag (A), 2026-10-04].
 * A React Router `useBlocker`-e továbbra sincs használatban (data routert igényel —
 * `useDataRouterContext` —, a projekt `BrowserRouter`-t használ, az átállás az egész alkalmazás
 * belépési pontját érintené). Helyette a `Dashboard.tsx` egy MODUL-SZINTEN bekötött `popstate`-
 * figyelővel (`installPopStateInterceptor`) a router ELŐTT kapja meg a böngésző Vissza/Előre gombját:
 *   LEFEDI: · a Dashboardon belüli Vissza/Előre-t (fül, Beállítások al-fül — `?ful=`/`al=`);
 *           · a Dashboardról KIVIVŐ Vissza/Előre-t (NEM Dashboard-útvonal, pl. `/helyszinek`,
 *             `/belepes` — `dashboardTabs.ts` `isDashboardPath`): mentetlen adat / nyitott kapu-kérdés /
 *             újra-belépő ablak mellett a címet a router `handlePop`-ja ELŐTT, szinkron visszaírja a
 *             Dashboardra (`pushState`) — a router így a Dashboard címét olvassa és nem bont; ez a
 *             tényleges védelem, a `stopImmediatePropagation` csak másodlagos [2. validátor-kör M-1,
 *             2026-10-04: a kivétele minden tesztben, a valódi Chrome-ban is zöld maradt] —, és
 *             ugyanez a kérdés jön.
 *   NEM FEDI LE: · a kódból indított útvonal-váltást (`navigate()`, `<Link>`) — ma a Dashboardról
 *             ilyen csak a kijelentkezés után (`AuthWrapper`) történik, és a kijelentkezés maga is a
 *             kapun megy át (`Dashboard.tsx` `handleLogout`); egy ÚJ, kódból indított kilépési út
 *             ezt a kaput NEM kapja meg automatikusan;
 *           · a Dashboardon KÍVÜLI képernyők Vissza-ját (ott a figyelő néma — nincs is mentetlen adat);
 *           · a sorrend a BEKÖTÉSI sorrenden múlik: MÉRVE (Chrome 140, 2026-10-04) a `window`-on a
 *             `capture: true` NEM sorol előre, a figyelők a bekötés sorrendjében futnak. Ezért a
 *             Dashboard modult statikusan kell importálni (`AuthWrapper.tsx`); egy `React.lazy`-s
 *             betöltés a router UTÁN kötne be, és a kifelé vivő Vissza védelme csendben elveszne.
 *             Ezt a `Dashboard.unsaved.test.tsx` FORRÁS-őre jelzi (az `AuthWrapper.tsx` statikus
 *             `import Dashboard from './Dashboard'` sorát és a `lazy(`/`import(` hiányát nézi); az
 *             E2E-re ebben nem építünk [2. validátor-kör M-3, 2026-10-04: a korábbi komment tévesen
 *             a `flow-url-state.spec.ts`-t nevezte meg ennek az őrének].
 * A lap bezárását/újratöltését a `beforeunload` fedi (ott a böngésző saját ablaka az EGYETLEN
 * lehetőség — tudatos kivétel a „nincs natív ablak" szabály alól).
 */

interface UnsavedSource {
  /** Emberi név a megerősítő ablak szövegéhez, pl. „Jelenléti ív". */
  label: string;
}

interface UnsavedChangesContextValue {
  /** Van-e BÁRMELYIK bejelentkezett képernyőnek mentetlen változása. */
  hasUnsaved: () => boolean;
  /** A mentetlen forrás(ok) neve — a megerősítő kérdés szövegéhez. */
  unsavedLabels: () => string[];
  /** Belső: a képernyők regisztrációja. */
  register: (id: number, source: UnsavedSource) => void;
  unregister: (id: number) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

let nextSourceId = 1;

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Ref, nem state: a kilépési pont a KATTINTÁS pillanatában kérdez, tehát mindig a friss
  // értéket kell látnia, és a regisztráció ne okozzon újrarenderelést a fél alkalmazásban.
  const sourcesRef = useRef<Map<number, UnsavedSource>>(new Map());
  // Csak a `beforeunload` bekötéséhez kell renderelés-szintű jelzés.
  const [anyDirty, setAnyDirty] = useState(false);

  const sync = useCallback(() => {
    setAnyDirty(sourcesRef.current.size > 0);
  }, []);

  const register = useCallback(
    (id: number, source: UnsavedSource) => {
      sourcesRef.current.set(id, source);
      sync();
    },
    [sync]
  );

  const unregister = useCallback(
    (id: number) => {
      sourcesRef.current.delete(id);
      sync();
    },
    [sync]
  );

  // A böngésző saját megerősítése CSAK amíg tényleg van mentetlen adat — különben a fejlesztői
  // újratöltés is kérdezne, és a felhasználó megszokná, hogy átkattint rajta.
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // A modern böngészők a szöveget nem jelenítik meg, de a visszatérési érték kell hozzá.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({
      hasUnsaved: () => sourcesRef.current.size > 0,
      unsavedLabels: () => Array.from(sourcesRef.current.values()).map((s) => s.label),
      register,
      unregister,
    }),
    [register, unregister]
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};

/**
 * A képernyő bejelenti, hogy van-e mentetlen változása. `isDirty === false` esetén a regisztráció
 * megszűnik, unmountnál szintén — így nem maradhat „szellem-piszkos" forrás a felületen.
 */
export function useUnsavedChanges(isDirty: boolean, label: string): void {
  const ctx = useContext(UnsavedChangesContext);
  const idRef = useRef<number | null>(null);

  if (idRef.current === null) {
    idRef.current = nextSourceId++;
  }
  const id = idRef.current;

  useEffect(() => {
    if (!ctx) return;
    if (isDirty) {
      ctx.register(id, { label });
    } else {
      ctx.unregister(id);
    }
    return () => ctx.unregister(id);
  }, [ctx, id, isDirty, label]);
}

/**
 * A kilépési pont (fül-váltás, képernyőn belüli váltás, dialógus-bezárás) ezzel kérdezi meg,
 * hogy szabad-e továbbmenni. Provider nélkül mindig `true` — egy hiányzó provider nem
 * akadályozhatja meg a navigációt (a védelem elmarad, de a felület használható marad).
 */
export function useUnsavedGuard(): () => string[] {
  const ctx = useContext(UnsavedChangesContext);
  return useCallback(() => (ctx ? ctx.unsavedLabels() : []), [ctx]);
}

export default UnsavedChangesProvider;
